import { useState, useEffect } from 'react';
import { useGetProductData } from 'hooks/useGetProductData';
import { trackClickEvent } from 'hooks/GTM4Analytics/useGTMAnalytics';
import { useSessionContext } from 'contexts/sessionProvider/SessionProvider';
import { GTM4_ANALYTICS_CONSTANTS, PROMO_CODE_STATE } from '../../constants';

type UsePromoCodeVerifierType = {
  isSuccess: boolean;
  isError: boolean;
  isLoading: boolean;
  state: string;
  clearState: () => void;
  updatePromoCodeBoxState: (inputPromoCode: string) => void;
};

const usePromoCodeBoxState = (): UsePromoCodeVerifierType => {
  const {
    productId, setIsAnnualPrice, promoCode, setPromoCode
  } = useSessionContext();
  const [inputPromoCode, setInputPromoCode] = useState('');
  const [promoCodeState, setPromoCodeState] = useState(promoCode ? PROMO_CODE_STATE.VALID : PROMO_CODE_STATE.EMPTY);
  const {
    productData, isLoading, isSuccess, isError
  } = useGetProductData({
    productId,
    promoCode: inputPromoCode
  });

  useEffect(() => {
    if (inputPromoCode && !isLoading) {
      if (isSuccess && productData?.items[0]?.deal?.promoCode?.toUpperCase() === inputPromoCode &&
          productData?.items[0]?.deal?.isPromoCodeBoxApplicable) {
        const promoCode = productData?.items[0]?.deal?.promoCode;
        setPromoCode(promoCode);
        setIsAnnualPrice(productData?.items[0]?.deal?.recurringInterval === 'ANNUAL');
        setPromoCodeState(PROMO_CODE_STATE.VALID);
        trackClickEvent(GTM4_ANALYTICS_CONSTANTS.VALID_PROMO_CODE_APPLIED, { promo_code: promoCode });
      } else {
        setPromoCodeState(PROMO_CODE_STATE.INVALID);
        trackClickEvent(GTM4_ANALYTICS_CONSTANTS.INVALID_PROMO_CODE_APPLIED, { promo_code: inputPromoCode });
      }
    }
  }, [isLoading, isSuccess, inputPromoCode]);

  const updatePromoCodeBoxState = (inputPromoCode: string) => {
    if (!inputPromoCode || inputPromoCode.trim() === '') {
      return;
    }
    setInputPromoCode(inputPromoCode.trim());
  };

  return {
    isSuccess,
    isError,
    isLoading,
    state: promoCodeState,
    clearState: () => {
      setInputPromoCode('');
      setPromoCodeState(PROMO_CODE_STATE.EMPTY);
    },
    updatePromoCodeBoxState
  };
};

export default usePromoCodeBoxState;
